import React, { useEffect, Suspense } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, User, MessageCircle, Sparkles } from 'lucide-react';
import articles from '../../content/articles-manifest.json';
import { COMPANY_BRAND_NAME } from '../../support/brand';
import { COMPANY_NAME, COMPANY_CNPJ } from '../../support/legal';
import { SAME_AS_LINKS } from '../../support/social';
import { PANEL_LEAD_URL } from '../../support/publicUrls';

const ArticleBody = React.lazy(() => import('./ArticleBody'));

export function getArticleStructuredData(article) {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      datePublished: article.date,
      dateModified: article.date,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://okmaia.com/artigos/${article.slug}`,
      },
      author: {
        '@type': 'Organization',
        name: COMPANY_BRAND_NAME,
        legalName: COMPANY_NAME,
        url: 'https://okmaia.com/',
        sameAs: SAME_AS_LINKS,
      },
      publisher: {
        '@type': 'Organization',
        name: COMPANY_BRAND_NAME,
        legalName: COMPANY_NAME,
        taxID: COMPANY_CNPJ,
        url: 'https://okmaia.com/',
        logo: 'https://okmaia.com/logo.png',
        sameAs: SAME_AS_LINKS,
      },
    },
  ];
}

export function renderArticleHeadTags(article) {
  const structuredData = getArticleStructuredData(article);
  const canonical = `https://okmaia.com/artigos/${article.slug}`;
  const markdownUrl = `https://okmaia.com/artigos/${article.slug}.md`;

  const tags = [
    `<title>${article.title} | MAIA</title>`,
    `<meta name="description" content="${article.description.replace(/"/g, '&quot;')}" />`,
    `<link rel="canonical" href="${canonical}" />`,
    `<link rel="alternate" type="text/markdown" href="${markdownUrl}" />`,
    `<meta property="og:title" content="${article.title.replace(/"/g, '&quot;')}" />`,
    `<meta property="og:description" content="${article.description.replace(/"/g, '&quot;')}" />`,
    `<meta property="og:url" content="${canonical}" />`,
    `<meta property="og:type" content="article" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${article.title.replace(/"/g, '&quot;')}" />`,
    `<meta name="twitter:description" content="${article.description.replace(/"/g, '&quot;')}" />`,
    `<script data-article-static="true" type="application/ld+json">${JSON.stringify(
      structuredData
    ).replaceAll('<', '\\u003c')}</script>`,
  ];

  return tags.join('\n    ');
}

export function renderArticleMarkdown(article) {
  return [
    `# ${article.title}`,
    '',
    `*Por ${article.author} em ${article.date} · ${article.category} · ${article.readTime}*`,
    '',
    `> ${article.description}`,
    '',
    article.content,
    '',
    '---',
    '',
    `## Sobre a ${COMPANY_BRAND_NAME}`,
    `A **${COMPANY_BRAND_NAME}** (${COMPANY_NAME} · CNPJ ${COMPANY_CNPJ}) é uma empresa brasileira de tecnologia focada na criação e operação de agentes de inteligência artificial autônomos.`,
    '',
    'Conheça as soluções da MAIA:',
    '- [Plataforma MAIA](https://okmaia.com/): criação, personalização de prompts, subagentes e orquestração de IA.',
    '- [Agente de IA para WhatsApp](https://okmaia.com/agente-ia-whatsapp): integração oficial à WhatsApp Business Cloud API para vendas, suporte e atendimento.',
    '- [Mentoria Digital](https://okmaia.com/mentor-ia): transforme infoprodutos, cursos e métodos em atendimento individual pelo WhatsApp.',
    '',
    'Saiba mais em: https://okmaia.com/',
  ].join('\n');
}

function ArticleShow({ article: propArticle }) {
  const { slug } = useParams();
  const article = propArticle || articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | MAIA`;
    }
  }, [article]);

  if (!article) {
    return <Navigate to="/artigos" replace />;
  }

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link
        to="/artigos"
        className="inline-flex items-center gap-2 text-sm font-medium text-forge-600 hover:text-forge-700 mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar para todos os artigos
      </Link>

      <header className="mb-10 pb-8 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-3 text-xs text-content-muted mb-4">
          <span className="inline-flex items-center gap-1 font-semibold text-forge-600 bg-forge-50 px-2.5 py-1 rounded-lg">
            <Tag className="w-3 h-3" />
            {article.category}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {article.date}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {article.readTime}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-3.5 h-3.5" />
            {article.author}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-content-primary leading-tight">
          {article.title}
        </h1>

        <p className="mt-6 text-lg sm:text-xl text-content-secondary leading-relaxed">
          {article.description}
        </p>
      </header>

      <Suspense
        fallback={
          <div className="report-markdown text-base sm:text-lg whitespace-pre-wrap">
            {article.content}
          </div>
        }
      >
        <ArticleBody content={article.content} />
      </Suspense>

      <footer className="mt-14 pt-8 border-t border-slate-200 space-y-8">
        {/* Bloco institucional permanente da MAIA para autoridade e SEO */}
        <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-6 sm:p-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-forge-600 mb-3">
            <Sparkles className="w-4 h-4" />
            Sobre a {COMPANY_BRAND_NAME}
          </div>
          <h3 className="text-xl font-bold text-content-primary">
            Tecnologia brasileira para agentes de inteligência artificial
          </h3>
          <p className="mt-2 text-sm text-content-secondary leading-relaxed">
            A <strong>{COMPANY_BRAND_NAME}</strong> ({COMPANY_NAME} · CNPJ {COMPANY_CNPJ}) desenvolve a plataforma de tecnologia que permite criar, configurar, testar e operar agentes de IA autônomos. Nossos agentes conectam-se à Cloud API oficial do WhatsApp, utilizam bases de conhecimento proprietárias e executam tarefas com total previsibilidade e transbordo humano.
          </p>

          <div className="mt-5 pt-4 border-t border-slate-200/80 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-content-muted">
            <span className="font-semibold text-content-primary">Conheça as soluções:</span>
            <Link to="/" className="text-forge-600 hover:text-forge-700 underline underline-offset-2">
              Plataforma de Agentes (Home)
            </Link>
            <Link to="/agente-ia-whatsapp" className="text-forge-600 hover:text-forge-700 underline underline-offset-2">
              Agente de IA para WhatsApp
            </Link>
            <Link to="/mentor-ia" className="text-forge-600 hover:text-forge-700 underline underline-offset-2">
              Mentoria Digital no WhatsApp
            </Link>
            <Link to="/artigos" className="text-forge-600 hover:text-forge-700 underline underline-offset-2">
              Mais artigos e guias
            </Link>
          </div>
        </section>

        {/* CTA comercial */}
        <div className="rounded-2xl border border-forge-200 bg-forge-50/60 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-bold text-content-primary">
              Gostou deste conteúdo? Experimente a MAIA
            </h3>
            <p className="mt-1 text-sm text-content-secondary">
              Crie agentes de IA inteligentes para WhatsApp com conhecimento próprio e transbordo
              humano.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a href={PANEL_LEAD_URL} className="btn-primary text-sm px-5 py-2.5">
              Criar agente
            </a>
            <a
              href="https://wa.me/5521971792112?text=Ol%C3%A1%2C%20li%20o%20artigo%20e%20gostaria%20de%20saber%20mais%20sobre%20a%20MAIA."
              target="_blank"
              rel="noreferrer"
              className="btn-secondary text-sm px-4 py-2.5 inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4 text-emerald-600" />
              WhatsApp
            </a>
          </div>
        </div>
      </footer>
    </article>
  );
}

export default ArticleShow;
